
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Home, FileText, Contact, LayoutDashboard, BarChart2, Settings, Users, BookOpen, BadgeCheck } from "lucide-react";
import { NavLinks } from "./NavLinks";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { User } from "@supabase/supabase-js";
import useAdminStatus from "@/hooks/useAdminStatus";

interface HeaderProps {
  user: User | null;
  isAuthEnabled: boolean;
  handleSignOut: () => Promise<void>;
}

const Header: React.FC<HeaderProps> = ({ user, isAuthEnabled, handleSignOut }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAdmin } = useAdminStatus();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleEnrollClick = () => {
    navigate("/blueprint");
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/" className="text-xl font-semibold text-techne-purple flex items-center gap-1.5">
              <svg width="28" height="28" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M9.92285 0.342773C7.44097 0.342773 5.42285 2.3609 5.42285 4.84277V30.0642C5.42285 32.5461 7.44097 34.5642 9.92285 34.5642H30.5446C33.0265 34.5642 35.0446 32.5461 35.0446 30.0642V23.2189L24.0752 12.2495L23.8352 12.0111L17.5095 5.68534L9.92285 5.68513V0.342773ZM9.92285 11.0851H14.4229V30.0642C14.4229 30.0642 14.4229 30.0642 14.4229 30.0642H9.92285C9.92285 30.0642 9.92285 30.0642 9.92285 30.0642V11.0851ZM18.9229 15.5851H30.5446L30.5446 30.0642C30.5446 30.0642 30.5446 30.0642 30.5446 30.0642H18.9229C18.9229 30.0642 18.9229 30.0642 18.9229 30.0642V15.5851Z" fill="#8B5CF6"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M0 8.62793C0 7.52336 0.89543 6.62793 2 6.62793H7.34311C8.44768 6.62793 9.34311 7.52336 9.34311 8.62793V26.2793C9.34311 27.3839 8.44768 28.2793 7.34311 28.2793H2C0.895431 28.2793 0 27.3839 0 26.2793V8.62793Z" fill="#9B87F5"/>
                <path d="M17.5095 5.68533L20.2341 8.40993L14.4229 14.2211H9.92285V11.0851H14.4229L17.5095 5.68533Z" fill="#9B87F5"/>
                <path d="M23.8352 12.0111L26.5598 14.7357L18.9229 22.3726V15.5851H25.7104L23.8352 12.0111Z" fill="#9B87F5"/>
              </svg>
              <span>BoardSight AI</span>
            </Link>
          </div>

          <div className="hidden md:flex md:items-center md:space-x-6">
            <Link to="/" className="text-gray-600 hover:text-techne-purple flex items-center gap-1">
              <Home size={18} />
              <span>Home</span>
            </Link>
            <Link to="/blueprint" className="text-gray-600 hover:text-techne-purple flex items-center gap-1">
              <FileText size={18} />
              <span>Blueprint</span>
            </Link>
            <Link to="/quiz" className="text-gray-600 hover:text-techne-purple flex items-center gap-1">
              <LayoutDashboard size={18} />
              <span>Assessment</span>
            </Link>
            <Link to="/insights" className="text-gray-600 hover:text-techne-purple flex items-center gap-1">
              <BarChart2 size={18} />
              <span>Insights</span>
            </Link>
            <Link to="/contact" className="text-gray-600 hover:text-techne-purple flex items-center gap-1">
              <Contact size={18} />
              <span>Contact Us</span>
            </Link>
            
            {/* Add admin dashboard link if user is admin */}
            {isAdmin && (
              <Link to="/admin" className="text-gray-600 hover:text-techne-purple flex items-center gap-1">
                <Settings size={18} />
                <span className="font-medium">Admin</span>
              </Link>
            )}
          </div>

          <div className="flex items-center">
            {user ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <div className="h-8 w-8 rounded-full bg-techne-purple text-white flex items-center justify-center">
                        {user.user_metadata?.first_name?.charAt(0) || user.email?.charAt(0) || "U"}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <div className="flex items-center justify-start gap-2 p-2">
                      <div className="flex flex-col space-y-1 leading-none">
                        <p className="font-medium text-sm">
                          {user.user_metadata?.first_name
                            ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`
                            : user.email}
                        </p>
                        {user.email && (
                          <p className="w-[200px] truncate text-sm text-gray-500">
                            {user.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard">Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/courses">My Courses</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile">Profile</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/certificates">My Certificates</Link>
                    </DropdownMenuItem>
                    
                    {/* Admin links section */}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Admin</DropdownMenuLabel>
                        <DropdownMenuGroup>
                          <DropdownMenuItem asChild>
                            <Link to="/admin">Admin Dashboard</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/admin/users">User Management</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/admin/courses">Course Management</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/admin/certificates">Certificate Management</Link>
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </>
                    )}
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={handleSignOut}>
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                {isAuthEnabled ? (
                  <>
                    <button 
                      onClick={handleLoginClick}
                      className="text-gray-600 hover:text-techne-purple"
                    >
                      Log in
                    </button>
                    <Button 
                      asChild 
                      size="sm"
                      onClick={handleEnrollClick}
                    >
                      <button>Enroll Now</button>
                    </Button>
                  </>
                ) : (
                  <Button asChild size="sm">
                    <Link to="/blueprint">Learn More</Link>
                  </Button>
                )}
              </div>
            )}

            <button
              className="ml-4 md:hidden"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="mt-4 pb-4 md:hidden">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                onClick={toggleMobileMenu}
              >
                <Home size={18} />
                <span>Home</span>
              </Link>
              <Link
                to="/blueprint"
                className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                onClick={toggleMobileMenu}
              >
                <FileText size={18} />
                <span>Blueprint</span>
              </Link>
              <Link
                to="/quiz"
                className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                onClick={toggleMobileMenu}
              >
                <LayoutDashboard size={18} />
                <span>Assessment</span>
              </Link>
              <Link
                to="/insights"
                className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                onClick={toggleMobileMenu}
              >
                <BarChart2 size={18} />
                <span>Insights</span>
              </Link>
              <Link
                to="/contact"
                className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                onClick={toggleMobileMenu}
              >
                <Contact size={18} />
                <span>Contact Us</span>
              </Link>
              
              {user && (
                <>
                  <Link
                    to="/courses"
                    className="text-gray-600 hover:text-techne-purple"
                    onClick={toggleMobileMenu}
                  >
                    Courses
                  </Link>
                  <Link
                    to="/dashboard"
                    className="text-gray-600 hover:text-techne-purple"
                    onClick={toggleMobileMenu}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/certificates"
                    className="text-gray-600 hover:text-techne-purple"
                    onClick={toggleMobileMenu}
                  >
                    My Certificates
                  </Link>
                </>
              )}
              
              {/* Add admin section in mobile menu */}
              {isAdmin && (
                <>
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-sm text-gray-500 uppercase font-semibold">Admin</span>
                  </div>
                  <Link
                    to="/admin"
                    className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                    onClick={toggleMobileMenu}
                  >
                    <Settings size={18} />
                    <span>Admin Dashboard</span>
                  </Link>
                  <Link
                    to="/admin/users"
                    className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                    onClick={toggleMobileMenu}
                  >
                    <Users size={18} />
                    <span>User Management</span>
                  </Link>
                  <Link
                    to="/admin/courses"
                    className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                    onClick={toggleMobileMenu}
                  >
                    <BookOpen size={18} />
                    <span>Course Management</span>
                  </Link>
                  <Link
                    to="/admin/certificates"
                    className="text-gray-600 hover:text-techne-purple flex items-center gap-1"
                    onClick={toggleMobileMenu}
                  >
                    <BadgeCheck size={18} />
                    <span>Certificate Management</span>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
